import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, pipe } from 'rxjs';
import {
  distinctUntilChanged,
  take,
  tap,
  timeout,
  finalize,
  filter,
  switchMap,
  map,
} from 'rxjs/operators';
import {
  FeatureToggleType,
  setPendingTasksCount,
  updateFeatureToggles,
} from './feature-toggles.actions';
import { FeatureTogglesPartialState } from './feature-toggles.reducer';
import {
  hasPendingTasks,
  allEnabledFeatures,
  featuresEnabled,
  QueryOperator,
  featureValue,
} from './feature-toggles.selectors';
import { ToggleValue } from './interfaces';

@Injectable({
  providedIn: 'root',
})
export class FeatureTogglesFacade {
  public allEnabledFeatureToggles$ = this.store$.pipe(
    select(allEnabledFeatures),
    notPending(this.store$),
  );

  constructor(private store$: Store<FeatureTogglesPartialState>) {}

  public updateFeatureToggles(
    toggles: Record<string, ToggleValue>,
    toggleType: FeatureToggleType,
    namespace?: string,
  ) {
    this.store$.dispatch(
      updateFeatureToggles({ toggles, toggleType, namespace }),
    );
  }

  public loadFeatureToggles(
    loader: () => Observable<Record<string, ToggleValue>>,
    type: FeatureToggleType,
    namespace?: string,
  ) {
    this.store$.dispatch(setPendingTasksCount({ increment: true }));

    return loader()
      .pipe(
        timeout(5000), // in case something hangs
        take(1),
        tap((r) => this.updateFeatureToggles(r, type, namespace)),
        finalize(() =>
          this.store$.dispatch(setPendingTasksCount({ increment: false })),
        ),
      )
      .toPromise();
  }

  public featuresEnabled(toggles: string[], operator: QueryOperator = 'AND') {
    return this.store$.pipe(
      select(featuresEnabled(toggles, operator)),
      notPending(this.store$),
      distinctUntilChanged(),
    );
  }

  public featureValue<T extends ToggleValue = ToggleValue>(
    toggle: string,
  ): Observable<T> {
    return this.store$.pipe(
      select(featureValue(toggle)),
      notPending(this.store$),
      distinctUntilChanged(),
    ) as Observable<T>;
  }
}

function notPending<T>(store$: Store<FeatureTogglesPartialState>) {
  return pipe(
    switchMap((source: T) =>
      store$.pipe(
        select(hasPendingTasks),
        map((pending) => [source, pending]),
      ),
    ),
    filter<[T, boolean]>(([_, pending]) => !pending),
    map(([src]) => src),
  );
}
